import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar/Navbar';
import { UnreadNoteCountProvider } from './hooks/useUnreadNoteCount';
import DeviceList from './components/DeviceList/DeviceList';
import LoginPage from './pages/LoginPage';
import AppUsers from './components/AppUsers';
import AppUserPage from './components/AppUserDetails/AppUserPage';
import AppUserThreadPage from './components/AppUserDetails/AppUserThreadPage';
import Enroll from './components/Enroll';
import RecentBundlesPage from './pages/RecentBundlesPage';
import FirestoreExplorerPage from './pages/FirestoreExplorerPage';
import FirmwarePage from './pages/FirmwarePage';
import FleetHealthPage from './pages/FleetHealthPage';
import UsersPage from './pages/UsersPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import FilterCatalogPage from './pages/FilterCatalogPage';
import EquipmentCatalogPage from './pages/EquipmentCatalogPage';
import EquipmentInventoryPage from './pages/EquipmentInventoryPage';
import DataCleanupConsolePage from './pages/DataCleanupConsolePage';
import FeaturesPage from './pages/FeaturesPage';
import AlertsPage from './pages/AlertsPage';
import DashboardsPage from './pages/DashboardsPage';
import SystemPage from './components/System/SystemPage';
import PatternsPage from './components/System/PatternsPage';
import TagsPage from './pages/TagsPage';
import DeviceIndex from './components/DeviceWorkspace/DeviceIndex';
import DeviceWorkspaceLayout from './components/DeviceWorkspace/DeviceWorkspaceLayout';
import DeviceWorkspaceTab from './components/DeviceWorkspace/DeviceWorkspaceTab';
import WorkspaceIndexRedirect from './components/DeviceWorkspace/WorkspaceIndexRedirect';
import { LegacyTabRedirect, LegacyPhotosRedirect } from './components/DeviceWorkspace/legacyRedirects';
import RequirePermission from './components/RequirePermission';
import LabelPhotoWindowPage from './pages/LabelPhotoWindowPage';
import { LABEL_PHOTO_WINDOW_PATH } from './utils/labelPhotoWindow';
import './index.css';

// Protected Route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn, authChecked } = useAuth();
  const location = useLocation();

  if (!authChecked) {
    return null;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  return <>{children}</>;
};

const LoginRedirect = () => {
  const { isLoggedIn, authChecked } = useAuth();
  const location = useLocation();
  if (!authChecked) return null;
  if (!isLoggedIn) return <LoginPage />;
  const from = (location.state as { from?: string } | null)?.from || '/devices';
  return <Navigate to={from} replace />;
};

const RootRedirect = () => {
  const { isLoggedIn, authChecked } = useAuth();
  if (!authChecked) return null;
  return <Navigate to={isLoggedIn ? '/devices' : '/login'} replace />;
};

/**
 * The console's navigation, except in a window that shows one label photo on
 * its own (FS-1079). The sidebar is a flex item of the page row itself, not of
 * a wrapper: a sticky element can only move inside its parent, and a wrapper
 * as wide as the sidebar left it no room to stay at the left edge when the
 * page scrolled sideways (FS-1162).
 */
const AppNavbar = () => {
  const { pathname } = useLocation();
  if (pathname === LABEL_PHOTO_WINDOW_PATH) return null;
  return <Navbar />;
};

const App: React.FC = () => {
  const { isLoggedIn } = useAuth();

  return (
    // Device List report/filter sequencing relies on synchronous router-state updates.
    <Router useTransitions={false}>
      <UnreadNoteCountProvider>
      <div className="min-h-screen flex flex-col lg:flex-row">
        {isLoggedIn && <AppNavbar />}
        <div
          data-console-content=""
          className={`
            grow min-w-0
            overflow-auto
            ${isLoggedIn ? 'ml-1' : ''}
          `}
        >
          <div className="h-full w-full">
            <Routes>
              {/* Protected routes */}
              <Route path="/edit-device" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_devices">
                    <div className="border-2">
                      <DeviceList />
                    </div>
                  </RequirePermission>
                </ProtectedRoute>
              } />
              {/* Plan 94 Phase 6: legacy per-tool routes now redirect into the
                  device-first workspace (sticky tab remembered). */}
              <Route path="/analyze" element={
                <ProtectedRoute><LegacyTabRedirect tab="analysis" /></ProtectedRoute>
              } />
              {/* Plan 94: device-first workspace + Fleet Explorer. */}
              <Route path="/devices" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_devices">
                    <DeviceIndex />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/devices/:deviceId" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_devices">
                    <DeviceWorkspaceLayout />
                  </RequirePermission>
                </ProtectedRoute>
              }>
                <Route index element={<WorkspaceIndexRedirect />} />
                <Route path=":tab" element={<DeviceWorkspaceTab />} />
              </Route>
              <Route path="/notes" element={
                <ProtectedRoute><LegacyTabRedirect tab="notes" /></ProtectedRoute>
              } />
              <Route path="/app-data" element={
                <ProtectedRoute>
                  <RequirePermission perm="firestore_read">
                    <FirestoreExplorerPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/app-users" element={
                <ProtectedRoute>
                  <RequirePermission perm="firestore_read">
                    <AppUsers />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/app-users/:uid" element={
                <ProtectedRoute><RequirePermission perm="firestore_read"><AppUserPage /></RequirePermission></ProtectedRoute>
              } />
              <Route path="/app-users/:uid/notes/:noteId" element={
                <ProtectedRoute><RequirePermission perm="firestore_read"><AppUserThreadPage /></RequirePermission></ProtectedRoute>
              } />
              <Route path="/enroll" element={
                <ProtectedRoute>
                  <RequirePermission perm="enroll">
                    <Enroll />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/debug" element={
                <ProtectedRoute><LegacyTabRedirect tab="debug" /></ProtectedRoute>
              } />
              <Route path="/read-bundles" element={
                <ProtectedRoute>
                  <RequirePermission perm="debug">
                    <RecentBundlesPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path={LABEL_PHOTO_WINDOW_PATH} element={
                <ProtectedRoute><LabelPhotoWindowPage /></ProtectedRoute>
              } />
              <Route path="/photos" element={
                <ProtectedRoute><LegacyPhotosRedirect /></ProtectedRoute>
              } />
              <Route path="/firmware" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_firmware">
                    <FirmwarePage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/filter-catalog" element={
                <ProtectedRoute>
                  <RequirePermission perm="filters">
                    <FilterCatalogPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              {/* Plan 119 Phase 3 (FS-627): the equipment model catalog the
                  resolver reads, so an operator can see the manufacturer
                  document behind a value. Read-only. */}
              <Route path="/equipment-catalog" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_devices">
                    <EquipmentCatalogPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              {/* Plan 119 Phase 2 (FS-460): the field-level equipment
                  inventory ops works, and FS-600 selects recipients from. */}
              <Route path="/equipment-inventory" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_devices">
                    <EquipmentInventoryPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/fleet-health" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_health">
                    <FleetHealthPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/data-cleanup" element={
                <ProtectedRoute>
                  <RequirePermission perm="preview_data_cleanup">
                    <DataCleanupConsolePage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              {/* FS-496: app feature-flag administration. */}
              <Route path="/features" element={
                <ProtectedRoute>
                  <RequirePermission perm="admin_users">
                    <FeaturesPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute>
                  <RequirePermission perm="admin_users">
                    <UsersPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/alerts" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_alerts">
                    <AlertsPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/dashboards" element={
                <ProtectedRoute>
                  <RequirePermission perm="view_dashboards">
                    <DashboardsPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/tags" element={
                <ProtectedRoute>
                  <RequirePermission perm="read_devices">
                    <TagsPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/system" element={
                <ProtectedRoute>
                  <RequirePermission perm="system_admin">
                    <SystemPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />
              <Route path="/system/patterns" element={
                <ProtectedRoute>
                  <RequirePermission perm="system_admin">
                    <PatternsPage />
                  </RequirePermission>
                </ProtectedRoute>
              } />

              {/* Public routes */}
              <Route path="/login" element={<LoginRedirect />} />
              {/* Plan 28: token-link password setup + self-service reset.
                  Deliberately outside ProtectedRoute — the emailed token is
                  the credential, carried in the URL fragment (#token=...) so
                  it never reaches server logs. */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              {/* Default route */}
              <Route path="/" element={<RootRedirect />} />

              {/* Catch all other routes */}
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </div>
        </div>
      </div>
      </UnreadNoteCountProvider>
    </Router>
  );
};

export default App;
